// components/Tips/Tip.js
import React, { useEffect, useRef, useState } from 'react';
import { useTips } from './TipContext';
import { Trans, useTranslation } from 'react-i18next';
import './Tip.css';


const Tip = ({
    id,
    text,
    targetId,
    position = 'bottom',
    onComplete,
    delay = 0,
    btnText = 'Next'
}) => {
    const { t } = useTranslation();

    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const tipRef = useRef(null);
    const { completedTips, markTipComplete, activeTip, setActiveTip, hideTips, updateHideTips } = useTips();

    useEffect(() => {
        if (!completedTips[id] && activeTip === id) {
            const timer = setTimeout(() => {
                setIsVisible(true);
                positionTip();
            }, delay);
            return () => clearTimeout(timer);
        }
    }, [id, completedTips, activeTip, delay]);

    const positionTip = () => {
        const targetElement = document.getElementById(targetId);
        if (!targetElement || !tipRef.current) return;

        const targetRect = targetElement.getBoundingClientRect();
        const tipRect = tipRef.current.getBoundingClientRect();
        const spacing = 10;

        let top, left;
        switch (position) {
            case 'top':
                top = targetRect.top - tipRect.height - spacing;
                left = targetRect.left + (targetRect.width - tipRect.width) / 2;
                break;
            case 'bottom':
                top = targetRect.bottom + spacing;
                left = targetRect.left + (targetRect.width - tipRect.width) / 2;
                break;
            case 'left':
                top = targetRect.top + (targetRect.height - tipRect.height) / 2;
                left = targetRect.left - tipRect.width - spacing;
                break;
            case 'right':
                top = targetRect.top + (targetRect.height - tipRect.height) / 2;
                left = targetRect.right + spacing;
                break;
            default:
                top = targetRect.bottom + spacing;
                left = targetRect.left + (targetRect.width - tipRect.width) / 2;
        }

        setCoords({
            top: Math.max(0, top),
            left: Math.max(0, left),
            targetRect
        });
    };

    useEffect(() => {
        if (isVisible) {
            window.addEventListener('resize', positionTip);
            window.addEventListener('scroll', positionTip);
            return () => {
                window.removeEventListener('resize', positionTip);
                window.removeEventListener('scroll', positionTip);
            };
        }
    }, [isVisible]);

    const handleClose = () => {
        setIsVisible(false);
        markTipComplete(id);
        setActiveTip(null);
        onComplete?.();
    };

    if (!isVisible || completedTips[id] || activeTip !== id || hideTips) return null;

    return (
        <>
            <div
                ref={tipRef}
                className={`tip-container ${position}`}
                style={{
                    position: 'fixed',
                    top: coords.top,
                    left: coords.left,
                }}
            >
                <div className="tip-content">
                    <p>{ t(text) }</p>

                    <div className="tip-buttons">
                        <button onClick={handleClose} className="next-button">{ t(btnText) }</button>
                        <button onClick={updateHideTips} className="hide-button"><Trans>Don't show again</Trans></button>
                    </div>
                </div>
                <div className={`tip-arrow ${position}`} />
            </div>
            {coords.targetRect && (
                <div className="tip-overlay">
                    <div className="tip-backdrop" />
                    <div
                        className="tip-highlight"
                        style={{
                            position: 'fixed',
                            top: coords.targetRect.top - 4,
                            left: coords.targetRect.left - 4,
                            width: coords.targetRect.width + 8,
                            height: coords.targetRect.height + 8
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default Tip;
